import { useState, useEffect } from 'react';
import { FOCUSABLE_ELEMENTS } from '../../system/index.js';
const isElementAvailable = (element) => {
    const style = window.getComputedStyle(element);
    return (!element.hasAttribute('disabled') &&
        !element.hasAttribute('readonly') &&
        style.getPropertyValue('display') !== 'none');
};
export const useKeyboardNavigation = (ref, children, onClose) => {
    const activeElement = document.activeElement;
    useEffect(() => () => {
        activeElement?.focus();
    }, []);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [focusableElements, setFocusableElements] = useState([]);
    function focusElement(elementIndex) {
        if (focusableElements[elementIndex]) {
            focusableElements[elementIndex].focus();
        }
    }
    function getNextAvailableElementIndex() {
        const nextAvailableElementIndex = focusableElements.findIndex((element, index) => isElementAvailable(element) && index >= activeIndex + 1);
        return nextAvailableElementIndex != -1 ? nextAvailableElementIndex : 0;
    }
    function getPrevAvailableElementIndex() {
        const prevAvailableElementIndex = focusableElements.findLastIndex((element, index) => isElementAvailable(element) && index <= activeIndex - 1);
        return prevAvailableElementIndex != -1 ? prevAvailableElementIndex : 0;
    }
    function setElements() {
        if (ref?.current) {
            setFocusableElements(Array.from(ref.current.querySelectorAll(...FOCUSABLE_ELEMENTS)));
        }
    }
    useEffect(() => {
        focusElement(activeIndex);
    }, [activeIndex]);
    useEffect(() => {
        setElements();
    }, [ref, children]);
    function handleClick(element, index) {
        if (element.isSameNode(document.activeElement)) {
            setActiveIndex(index);
        }
    }
    function handleTab(e) {
        e.preventDefault();
        const total = focusableElements.length;
        if (!e.shiftKey) {
            if (activeIndex + 1 === total) {
                setActiveIndex(0);
            }
            else {
                const nextActiveIndex = getNextAvailableElementIndex();
                setActiveIndex(nextActiveIndex);
            }
        }
        if (e.shiftKey) {
            if (activeIndex - 1 < 0) {
                setActiveIndex(total - 1);
            }
            else {
                const nextActiveIndex = getPrevAvailableElementIndex();
                setActiveIndex(nextActiveIndex);
            }
        }
    }
    function handleEscape(e) {
        e.preventDefault();
        onClose && onClose();
    }
    useEffect(() => {
        if (focusableElements?.[activeIndex]?.hasAttribute('disabled') ||
            focusableElements?.[activeIndex]?.hasAttribute('readonly')) {
            const nextActiveIndex = getNextAvailableElementIndex();
            setActiveIndex(nextActiveIndex);
        }
        focusableElements.forEach((element, index) => {
            element.addEventListener('click', () => handleClick(element, index));
        });
        return () => {
            focusableElements.forEach((element, index) => {
                element.removeEventListener('click', () => handleClick(element, index));
            });
        };
    }, [focusableElements]);
    const keyListenersMap = new Map([
        ['Tab', handleTab],
        ['Escape', handleEscape]
    ]);
    useEffect(() => {
        function handleKeydown(e) {
            const listener = keyListenersMap.get(e.key);
            if (listener) {
                listener(e);
            }
        }
        document.addEventListener('keydown', handleKeydown);
        return () => {
            document.removeEventListener('keydown', handleKeydown);
        };
    }, [handleTab, handleEscape]);
    return {
        setElements
    };
};
//# sourceMappingURL=index.js.map